import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Bold, Copy, Eye, Italic, Link2, Mail, Pencil, Plus, Send, Trash2, Type } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

const STATUS = {
  bozza: { label: "Bozza", cls: "text-taupe border-hair" },
  programmata: { label: "Programmata", cls: "text-amber-700 border-amber-700/40" },
  inviata: { label: "Inviata", cls: "text-emerald-700 border-emerald-700/40" },
};

const btn = "min-h-[40px] inline-flex items-center gap-1.5 text-[11px] tracking-[0.12em] uppercase px-3 py-2 border transition-colors disabled:opacity-50";
const sel = "h-11 border border-hair bg-white px-3 text-sm rounded-none w-full";

const emptyCampaign = { name: "", subject: "", preheader: "", body_html: "", cta_label: "", cta_url: "", recipients_filter: { mode: "all", segments: [], sources: [], manual_emails: [], excluded: [] } };

/* Editor visuale minimale: paragrafi, titoli, grassetto, corsivo, link (senza HTML obbligatorio) */
function BodyEditor({ value, onChange }) {
  const ref = useRef(null);
  const loaded = useRef(false);
  useEffect(() => {
    if (ref.current && !loaded.current) { ref.current.innerHTML = value || ""; loaded.current = true; }
  }, [value]);
  useEffect(() => { loaded.current = false; }, [value === "" ]);
  const cmd = (c, arg = null) => { ref.current?.focus(); document.execCommand(c, false, arg); onChange(ref.current.innerHTML); };
  const link = () => {
    const url = window.prompt("Incolla l'indirizzo del link (https://…)");
    if (url) cmd("createLink", url);
  };
  const tbtn = "min-w-[36px] min-h-[36px] inline-flex items-center justify-center border border-hair hover:border-ink hover:bg-bone text-xs transition-colors";
  return (
    <div>
      <div className="flex gap-1.5 flex-wrap mb-2" data-testid="camp-editor-toolbar">
        <button type="button" onClick={() => cmd("formatBlock", "p")} className={tbtn} title="Paragrafo"><Type size={13} /></button>
        <button type="button" onClick={() => cmd("formatBlock", "h2")} className={`${tbtn} font-serif font-bold`} title="Titolo">H2</button>
        <button type="button" onClick={() => cmd("formatBlock", "h3")} className={`${tbtn} font-serif`} title="Sottotitolo">H3</button>
        <button type="button" onClick={() => cmd("bold")} className={tbtn} title="Grassetto" data-testid="camp-ed-bold"><Bold size={13} /></button>
        <button type="button" onClick={() => cmd("italic")} className={tbtn} title="Corsivo"><Italic size={13} /></button>
        <button type="button" onClick={link} className={tbtn} title="Inserisci link" data-testid="camp-ed-link"><Link2 size={13} /></button>
        <button type="button" onClick={() => cmd("removeFormat")} className={tbtn} title="Pulisci formato">⌫</button>
      </div>
      <div
        ref={ref}
        contentEditable
        onInput={() => onChange(ref.current.innerHTML)}
        className="min-h-[220px] border border-hair bg-white p-4 text-sm leading-relaxed focus:outline-none focus:border-ink [&_h2]:font-serif [&_h2]:text-2xl [&_h2]:mt-4 [&_h2]:mb-2 [&_h3]:font-serif [&_h3]:text-xl [&_h3]:mt-3 [&_h3]:mb-2 [&_p]:mb-3 [&_a]:text-gold [&_a]:underline"
        data-testid="camp-body-editor"
      />
      <p className="text-[11px] text-taupe mt-1">Scrivi e formatta come in un documento: il bottone CTA si aggiunge con i campi dedicati qui sotto.</p>
    </div>
  );
}

export function CampaignsPanel() {
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null); // campagna in editor
  const [form, setForm] = useState(emptyCampaign);
  const [recipients, setRecipients] = useState(null); // {count, skipped, recipients[]}
  const [segs, setSegs] = useState([]);
  const [sources, setSources] = useState([]);
  const [preview, setPreview] = useState(null); // {html}
  const [previewMode, setPreviewMode] = useState("desktop");
  const [scheduleAt, setScheduleAt] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/campaigns");
      setList(data);
    } catch { toast.error("Errore caricamento newsletter"); }
  }, []);
  useEffect(() => { load(); }, []);

  useEffect(() => {
    api.get("/admin/newsletter/segments").then((r) => setSegs(r.data.segments || [])).catch(() => {});
  }, []);

  const openEditor = (c) => {
    setForm({
      name: c.name || "", subject: c.subject || "", preheader: c.preheader || "",
      body_html: c.body_html || "", cta_label: c.cta_label || "", cta_url: c.cta_url || "",
      recipients_filter: { mode: "all", segments: [], sources: [], manual_emails: [], excluded: [], ...(c.recipients_filter || {}) },
    });
    setScheduleAt(c.scheduled_at ? c.scheduled_at.slice(0, 16) : "");
    setEditing(c);
    setRecipients(null);
    refreshRecipients({ mode: "all", segments: [], sources: [], manual_emails: [], excluded: [], ...(c.recipients_filter || {}) });
  };

  const refreshRecipients = async (flt) => {
    try {
      const { data } = await api.post("/admin/campaigns/recipients-preview", flt || form.recipients_filter);
      setRecipients(data);
      const src = [...new Set((data.recipients || []).map((r) => r.source).filter(Boolean))];
      setSources((prev) => [...new Set([...prev, ...src])]);
    } catch { toast.error("Errore conteggio destinatari"); }
  };

  const setFilter = (patch) => {
    const f = { ...form.recipients_filter, ...patch };
    setForm({ ...form, recipients_filter: f });
    refreshRecipients(f);
  };

  const toggleExcluded = (email) => {
    const ex = new Set(form.recipients_filter.excluded || []);
    ex.has(email) ? ex.delete(email) : ex.add(email);
    setFilter({ excluded: [...ex] });
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Inserisci il nome interno della campagna"); return null; }
    setBusy(true);
    try {
      const { data } = editing?.id && editing.status !== "__new__"
        ? await api.put(`/admin/campaigns/${editing.id}`, form)
        : await api.post("/admin/campaigns", form);
      toast.success("Bozza salvata");
      setEditing(data);
      load();
      return data;
    } catch (err) { toast.error(err.response?.data?.detail || "Errore salvataggio"); return null; }
    finally { setBusy(false); }
  };

  const createNew = () => openEditor({ status: "__new__" });

  const duplicate = async (c) => {
    try { await api.post(`/admin/campaigns/${c.id}/duplicate`); toast.success("Newsletter duplicata"); load(); }
    catch { toast.error("Duplicazione non riuscita"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Eliminare definitivamente la newsletter "${c.name}"?`)) return;
    try { await api.delete(`/admin/campaigns/${c.id}`); toast.success("Newsletter eliminata"); if (editing?.id === c.id) setEditing(null); load(); }
    catch { toast.error("Eliminazione non riuscita"); }
  };

  const openPreview = async () => {
    const saved = await save();
    if (!saved) return;
    try { const { data } = await api.get(`/admin/campaigns/${saved.id}/preview`); setPreview(data); setPreviewMode("desktop"); }
    catch { toast.error("Anteprima non disponibile"); }
  };

  const sendTest = async () => {
    const saved = await save();
    if (!saved) return;
    try { const { data } = await api.post(`/admin/campaigns/${saved.id}/test`); toast.success(`Email di prova inviata a ${data.to}`); }
    catch (err) { toast.error(err.response?.data?.detail || "Invio di prova non riuscito"); }
  };

  const doSchedule = async () => {
    const saved = await save();
    if (!saved) return;
    try {
      const { data } = await api.post(`/admin/campaigns/${saved.id}/schedule`, { at: scheduleAt ? new Date(scheduleAt).toISOString() : "" });
      toast.success(data.status === "programmata" ? `Invio programmato: ${fmtDate(data.scheduled_at)}` : "Programmazione rimossa");
      setEditing({ ...saved, ...data }); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Programmazione non riuscita"); }
  };

  const sendNow = async () => {
    const saved = await save();
    if (!saved) return;
    const n = recipients?.count ?? 0;
    if (!window.confirm(`Stai per inviare questa newsletter a ${n} contatti. Confermi?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/campaigns/${saved.id}/send`);
      toast.success(`Newsletter inviata: ${data.stats.sent} recapiti, ${data.stats.failed} errori, ${data.stats.skipped} esclusi senza consenso`);
      setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Invio non riuscito"); }
    finally { setBusy(false); }
  };

  const flt = form.recipients_filter;
  const arrToggle = (key, v) => {
    const s = new Set(flt[key] || []);
    s.has(v) ? s.delete(v) : s.add(v);
    setFilter({ [key]: [...s] });
  };

  return (
    <div className="space-y-6" data-testid="campaigns-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Newsletter manuali</div>
          <p className="text-xs text-taupe mt-1">Crea e invia newsletter ai contatti con consenso marketing. Le automazioni esistenti restano attive e invariate.</p>
        </div>
        <button onClick={createNew} className={`${btn} bg-ink text-bone border-ink hover:bg-gold hover:text-ink`} data-testid="camp-new"><Plus size={14} /> Nuova Newsletter</button>
      </div>

      {/* Lista campagne */}
      {list === null ? <p className="text-taupe text-sm">Caricamento…</p> : (
        <div className="space-y-2">
          {list.length === 0 && <div className="p-6 text-center text-taupe bg-white border border-hair text-sm">Nessuna newsletter creata.</div>}
          {list.map((c) => (
            <div key={c.id} className="bg-white border border-hair p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`camp-row-${c.id}`}>
              <div className="min-w-0">
                <div className="font-serif text-lg leading-snug">{c.name}</div>
                <div className="text-xs text-taupe mt-0.5">{c.subject || <em>senza oggetto</em>} · aggiornata {fmtDate(c.updated_at)}</div>
                {c.status === "programmata" && <div className="text-xs text-amber-700 mt-0.5">Invio programmato: {fmtDate(c.scheduled_at)}</div>}
                {c.status === "inviata" && c.stats && <div className="text-xs text-emerald-700 mt-0.5">Inviata il {fmtDate(c.sent_at)} · {c.stats.sent ?? 0}/{c.stats.total ?? 0} recapiti</div>}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] tracking-[0.12em] uppercase border px-2 py-1 ${STATUS[c.status]?.cls || ""}`} data-testid={`camp-status-${c.id}`}>{STATUS[c.status]?.label || c.status}</span>
                {c.status !== "inviata" && <button onClick={() => openEditor(c)} className={`${btn} border-ink hover:bg-ink hover:text-bone`} data-testid={`camp-edit-${c.id}`}><Pencil size={13} /> Modifica</button>}
                <button onClick={() => duplicate(c)} className={`${btn} border-hair hover:border-ink`} data-testid={`camp-dup-${c.id}`}><Copy size={13} /> Duplica</button>
                <button onClick={() => remove(c)} className={`${btn} border-red-700 text-red-700 hover:bg-red-700 hover:text-bone`} data-testid={`camp-del-${c.id}`}><Trash2 size={13} /> Elimina</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor campagna */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-none max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="camp-editor-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editing?.id ? "Modifica newsletter" : "Nuova newsletter"}</DialogTitle></DialogHeader>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Nome interno campagna</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none mt-1" placeholder="Es. Newsletter Ottobre" data-testid="camp-name" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Oggetto email</Label>
              <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="rounded-none mt-1" data-testid="camp-subject" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Preheader <span className="normal-case tracking-normal">(facoltativo — anteprima nella casella di posta)</span></Label>
              <Input value={form.preheader} onChange={(e) => setForm({ ...form, preheader: e.target.value })} className="rounded-none mt-1" data-testid="camp-preheader" />
            </div>
          </div>
          <div className="mt-4">
            <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe block mb-2">Corpo email</Label>
            <BodyEditor key={editing?.id || "new"} value={form.body_html} onChange={(html) => setForm((f) => ({ ...f, body_html: html }))} />
          </div>
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <div>
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Testo pulsante CTA <span className="normal-case tracking-normal">(facoltativo)</span></Label>
              <Input value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} className="rounded-none mt-1" placeholder="Es. Scopri lo Shop" data-testid="camp-cta-label" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Link pulsante CTA</Label>
              <Input value={form.cta_url} onChange={(e) => setForm({ ...form, cta_url: e.target.value })} className="rounded-none mt-1" placeholder="https://…" data-testid="camp-cta-url" />
            </div>
          </div>

          {/* Destinatari */}
          <div className="mt-6 border-t border-hair pt-5">
            <div className="eyebrow mb-3">Destinatari</div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <select value={flt.mode} onChange={(e) => setFilter({ mode: e.target.value })} className={sel} data-testid="camp-recipients-mode">
                  <option value="all">Tutti i contatti con consenso marketing</option>
                  <option value="segments">Per segmento CRM</option>
                  <option value="sources">Per sorgente contatto</option>
                  <option value="manual">Selezione manuale (email)</option>
                </select>
                {flt.mode === "segments" && (
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="camp-segments">
                    {segs.map((s) => (
                      <button key={s.name} type="button" onClick={() => arrToggle("segments", s.name)}
                        className={`px-3 py-2 text-xs border transition-colors ${flt.segments?.includes(s.name) ? "bg-ink text-bone border-ink" : "border-hair hover:border-ink"}`}>
                        {s.name} ({s.count})
                      </button>
                    ))}
                  </div>
                )}
                {flt.mode === "sources" && (
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="camp-sources">
                    {sources.map((s) => (
                      <button key={s} type="button" onClick={() => arrToggle("sources", s)}
                        className={`px-3 py-2 text-xs border transition-colors ${flt.sources?.includes(s) ? "bg-ink text-bone border-ink" : "border-hair hover:border-ink"}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                )}
                {flt.mode === "manual" && (
                  <Textarea rows={3} className="rounded-none mt-3" placeholder="Una email per riga. Solo i contatti con consenso marketing saranno inclusi."
                    value={(flt.manual_emails || []).join("\n")}
                    onChange={(e) => setForm({ ...form, recipients_filter: { ...flt, manual_emails: e.target.value.split(/[\n,;]+/) } })}
                    onBlur={() => refreshRecipients()}
                    data-testid="camp-manual-emails" />
                )}
              </div>
              <div className="bg-bone border border-hair p-4" data-testid="camp-recipients-preview">
                <div className="font-serif text-3xl" data-testid="camp-recipients-count">Numero destinatari: {recipients ? recipients.count : "…"}</div>
                {recipients?.skipped > 0 && <p className="text-xs text-amber-700 mt-1">{recipients.skipped} contatti esclusi automaticamente: privi di consenso marketing.</p>}
                {flt.mode !== "manual" && recipients?.recipients?.length > 0 && (
                  <div className="mt-3 max-h-40 overflow-y-auto space-y-1">
                    <p className="text-[10px] tracking-[0.12em] uppercase text-taupe">Tocca un contatto per escluderlo/includerlo</p>
                    {recipients.recipients.map((r) => {
                      const off = (flt.excluded || []).includes(r.email);
                      return (
                        <button key={r.email} type="button" onClick={() => toggleExcluded(r.email)}
                          className={`block w-full text-left text-xs px-2 py-1.5 border transition-colors ${off ? "border-red-300 text-red-700 line-through bg-red-50" : "border-transparent hover:border-hair"}`}
                          data-testid={`camp-rcpt-${r.email}`}>
                          {r.name ? `${r.name} · ` : ""}{r.email}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Azioni */}
          <div className="mt-6 border-t border-hair pt-5 flex flex-wrap gap-2 items-center">
            <button onClick={save} disabled={busy} className={`${btn} border-ink hover:bg-ink hover:text-bone`} data-testid="camp-save">{busy ? "Salvataggio…" : "Salva bozza"}</button>
            <button onClick={openPreview} className={`${btn} border-hair hover:border-ink`} data-testid="camp-preview"><Eye size={13} /> Anteprima</button>
            <button onClick={sendTest} className={`${btn} border-hair hover:border-ink`} data-testid="camp-test"><Mail size={13} /> Invia prova a me</button>
            <div className="flex items-center gap-2">
              <input type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} className="h-10 border border-hair bg-white px-2 text-xs rounded-none" data-testid="camp-schedule-at" />
              <button onClick={doSchedule} className={`${btn} border-hair hover:border-ink`} data-testid="camp-schedule">Programma</button>
            </div>
            <button onClick={sendNow} disabled={busy} className={`${btn} bg-ink text-bone border-ink hover:bg-gold hover:text-ink ml-auto`} data-testid="camp-send"><Send size={13} /> Invia newsletter</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Anteprima desktop/mobile */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="rounded-none max-w-3xl" data-testid="camp-preview-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Anteprima newsletter</DialogTitle></DialogHeader>
          <div className="flex gap-2 mb-3">
            <button onClick={() => setPreviewMode("desktop")} className={`${btn} ${previewMode === "desktop" ? "bg-ink text-bone border-ink" : "border-hair"}`} data-testid="camp-preview-desktop">Desktop</button>
            <button onClick={() => setPreviewMode("mobile")} className={`${btn} ${previewMode === "mobile" ? "bg-ink text-bone border-ink" : "border-hair"}`} data-testid="camp-preview-mobile">Mobile</button>
          </div>
          <div className="bg-bone border border-hair p-4 flex justify-center">
            <iframe title="Anteprima" srcDoc={preview?.html || ""} style={{ width: previewMode === "desktop" ? "100%" : 375, height: 480, background: "#fff", border: "1px solid #e5e0d8" }} data-testid="camp-preview-frame" />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
